import dayjs from 'dayjs'
import { ChartTimeUnit } from 'types'

const UNIT_MS: Partial<Record<ChartTimeUnit, number>> = {
  seconds: 1000,
  minutes: 60_000,
  hours: 3_600_000,
  days: 86_400_000,
  weeks: 604_800_000,
}

/**
 * Snaps an instant down to the start of the chart's current aggregation bucket.
 *
 * Live tail re-anchors its window to `now` on every tick. At millisecond
 * precision that yields a brand-new range each time, even though the data is
 * bucketed server-side (`toStartOfInterval`) and is byte-identical within a
 * bucket — so the cache could never hit and the chart churned constantly.
 * Snapping makes ticks inside one bucket produce an identical range, which lets
 * the caller bail out entirely.
 *
 * Months are deliberately unsupported: they are not a fixed number of
 * milliseconds, and no live-tail window is bucketed that coarsely. Unknown or
 * absent units fall through to the unsnapped instant, preserving the previous
 * behaviour rather than guessing.
 */
export const snapToBucket = (
  instant: dayjs.Dayjs,
  interval?: number,
  unit?: ChartTimeUnit
): dayjs.Dayjs => {
  if (!interval || interval <= 0 || !unit) return instant
  const unitMs = UNIT_MS[unit]
  if (!unitMs) return instant
  const bucketMs = interval * unitMs
  return dayjs(Math.floor(instant.valueOf() / bucketMs) * bucketMs)
}

export interface LiveTailWindowInput {
  now: dayjs.Dayjs
  relativeStart: { value: number; unit: ChartTimeUnit }
  relativeEnd?: { isNow: boolean; value?: number; unit: ChartTimeUnit }
  interval?: number
  intervalUnit?: ChartTimeUnit
}

/**
 * The ISO window a live-tail poll should request. Both ends slide with `now`,
 * so the start moves too — that is why identifying a poll by "the start didn't
 * change" never worked.
 */
export const computeLiveTailWindow = ({
  now,
  relativeStart,
  relativeEnd,
  interval,
  intervalUnit,
}: LiveTailWindowInput): { from: string; to: string } => {
  const anchor = snapToBucket(now, interval, intervalUnit)
  const from = anchor.subtract(relativeStart.value, relativeStart.unit)
  const to = relativeEnd?.isNow
    ? anchor
    : anchor.subtract(relativeEnd?.value || 0, relativeEnd?.unit || 'days')

  return { from: from.toISOString(), to: to.toISOString() }
}
